import { useRef, useState, useEffect, ReactNode, Children } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

interface HorizontalScrollProps {
  children: ReactNode;
}

export default function HorizontalScroll({ children }: HorizontalScrollProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const isMobile = useIsMobile();
  const childCount = Children.count(children);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 2);

    // Calculate active dot index on mobile
    if (el.children.length > 0) {
      const firstChild = el.children[0] as HTMLElement;
      const cardWidth = firstChild.offsetWidth;
      const gap = parseFloat(getComputedStyle(el).columnGap) || 0;
      const idx = Math.round(el.scrollLeft / (cardWidth + gap));
      setActiveIndex(Math.min(idx, childCount - 1));
    }
  };

  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", checkScroll, { passive: true });
    const ro = new ResizeObserver(checkScroll);
    ro.observe(el);
    return () => { el.removeEventListener("scroll", checkScroll); ro.disconnect(); };
  }, [children]);

  const scroll = (dir: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = el.clientWidth;
    el.scrollBy({ left: dir === "left" ? -amount : amount, behavior: "smooth" });
  };

  return (
    <div className="group/scroll relative">
      {/* Left arrow */}
      {canScrollLeft && (
        <button
          onClick={() => scroll("left")}
          className="absolute -left-4 top-1/2 z-20 hidden -translate-y-1/2 items-center justify-center rounded-full bg-background/90 p-2 shadow-lg border border-border backdrop-blur-sm transition-opacity hover:bg-background md:flex"
          aria-label="Przewiń w lewo"
        >
          <ChevronLeft className="h-5 w-5 text-foreground" />
        </button>
      )}

      {/* Right arrow */}
      {canScrollRight && (
        <button
          onClick={() => scroll("right")}
          className="absolute -right-4 top-1/2 z-20 hidden -translate-y-1/2 items-center justify-center rounded-full bg-background/90 p-2 shadow-lg border border-border backdrop-blur-sm transition-opacity hover:bg-background md:flex"
          aria-label="Przewiń w prawo"
        >
          <ChevronRight className="h-5 w-5 text-foreground" />
        </button>
      )}

      {/* Scrollable row */}
      <div
        ref={scrollRef}
        className="grid grid-flow-col auto-cols-[100%] gap-3 overflow-x-scroll [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:auto-cols-[calc((100%-4*1rem)/5)] md:gap-4 md:overflow-x-auto"
        style={{ scrollSnapType: isMobile ? "x mandatory" : undefined }}
      >
        {Children.map(children, (child) => (
          <div className="min-w-full w-full md:min-w-0 md:w-auto" style={{ scrollSnapAlign: isMobile ? "start" : undefined }}>
            {child}
          </div>
        ))}
      </div>

      {/* Pagination dots – mobile only */}
      {isMobile && childCount > 1 && (
        <div className="mt-3 flex justify-center gap-1.5">
          {Array.from({ length: childCount }).map((_, i) => (
            <span
              key={i}
              className={`inline-block h-1.5 w-1.5 rounded-full transition-colors ${
                i === activeIndex ? "bg-primary" : "bg-muted-foreground/30"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
